import { PauseFilled } from '@app/components/icons/pause-filled';
import { PlayFilled } from '@app/components/icons/play-filled';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { BlurredVideoOverlay } from '../../../../components/blurred-video-overlay';
import { EditCaptionOverlay } from './components/edit-caption-overlay';
import { type TimelineZoomScale } from './edit-view-player-context';
import { SegmentVideoPlayerControls } from './segment-video-player-controls';
import { useEditPortraitBackground } from './use-edit-portrait-background';
import type { EditedSegment } from './utils/cuts-to-segments';

export type SegmentVideoPlayerRecording = {
  id: string;
  hlsUrl: string;
};

export type SegmentVideoPlayerPlaybackState = {
  outputTime: number;
  /** Original media time within the active segment (for transcript/captions). */
  sourceTimeSec: number;
  /** Source recording id for the active segment (future multi-recording edits). */
  recordingId: string;
  isPlaying: boolean;
};

export type SegmentVideoPlayerHandle = {
  play: () => void;
  pause: () => void;
  seek: (outputTime: number) => void;
  previewSegmentTime: (segmentId: string, sourceTimeSec: number) => void;
};

type SegmentVideoPlayerProps = {
  recordings: SegmentVideoPlayerRecording[];
  segments: EditedSegment[];
  outputDuration: number;
  onPlaybackStateChange?: (state: SegmentVideoPlayerPlaybackState) => void;
  zoomScale?: TimelineZoomScale;
  onZoomScaleChange?: (nextScale: TimelineZoomScale) => void;
  onSplitAtPlayhead?: () => void;
  canSplitAtPlayhead?: boolean;
  hints?: string[];
  className?: string;
};

type ActiveSegmentState = {
  segmentIndex: number;
  sourceTimeSec: number;
};

const toSeconds = (valueMs: number): number => valueMs / 1000;
const playMedia = (media: HTMLVideoElement | null) => {
  if (!media || typeof media.play !== 'function') {
    return;
  }
  void media.play();
};
const pauseMedia = (media: HTMLVideoElement | null) => {
  if (!media || typeof media.pause !== 'function') {
    return;
  }
  media.pause();
};

const findSegmentAtOutputTime = (
  activeSegments: EditedSegment[],
  outputTimeSec: number,
): ActiveSegmentState | null => {
  const outputTimeMs = outputTimeSec * 1000;

  for (let index = 0; index < activeSegments.length; index += 1) {
    const segment = activeSegments[index];
    if (
      outputTimeMs >= segment.outputStartTime &&
      outputTimeMs <= segment.outputEndTime
    ) {
      const offsetMs = outputTimeMs - segment.outputStartTime;
      return {
        segmentIndex: index,
        sourceTimeSec: (segment.originalStartTime + offsetMs) / 1000,
      };
    }
  }

  if (activeSegments.length === 0) {
    return null;
  }

  const lastIndex = activeSegments.length - 1;
  return {
    segmentIndex: lastIndex,
    sourceTimeSec: activeSegments[lastIndex].originalEndTime / 1000,
  };
};

type SegmentMediaItemProps = {
  segment: EditedSegment;
  recording: SegmentVideoPlayerRecording;
  isActive: boolean;
  isPortrait: boolean;
  isBlurMode: boolean;
  onRef: (node: Element | null) => void;
};

/**
 * A single segment's video element. Wrapped in React.memo so that only the
 * segment whose `isActive` flag changes re-renders when the active index
 * changes — inactive <hls-video> elements stay frozen.
 */
const SegmentMediaItem = memo(
  ({
    segment,
    recording,
    isActive,
    isPortrait,
    isBlurMode,
    onRef,
  }: SegmentMediaItemProps) => {
    const { t } = useTranslation();

    return (
      <div
        className={cx('absolute inset-0', {
          'z-10 opacity-100': isActive,
          'pointer-events-none z-0 opacity-0': !isActive,
        })}
      >
        <media-controller
          class={cx(
            'h-full w-full overflow-hidden rounded bg-transparent',
            isPortrait && 'relative z-10',
            isPortrait && isBlurMode && 'portrait-blur-bg',
          )}
        >
          <hls-video
            ref={onRef}
            slot="media"
            src={recording.hlsUrl}
            tabIndex={isActive ? 0 : -1}
            aria-hidden={!isActive}
            crossorigin
            playsinline
            preload="metadata"
            class={cx(
              'h-full w-full object-contain',
              isPortrait ? 'bg-transparent' : 'bg-black',
            )}
            data-testid={
              isActive
                ? 'segment-video-player-video'
                : `segment-video-player-video-${segment.id}`
            }
            aria-label={t('content-recording.editor-v2.segment-player-video')}
          />
        </media-controller>
      </div>
    );
  },
);

SegmentMediaItem.displayName = 'SegmentMediaItem';

export const SegmentVideoPlayer = forwardRef<
  SegmentVideoPlayerHandle,
  SegmentVideoPlayerProps
>(
  (
    {
      recordings,
      segments,
      outputDuration,
      onPlaybackStateChange,
      zoomScale = 1,
      onZoomScaleChange,
      onSplitAtPlayhead,
      canSplitAtPlayhead = true,
      hints,
      className,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const { isPortrait, isBlurMode, portraitBgColor } =
      useEditPortraitBackground();
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const mediaRefs = useRef<Map<string, HTMLVideoElement>>(new Map());
    const [videoElementForOverlay, setVideoElementForOverlay] =
      useState<HTMLElement | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [outputTime, setOutputTime] = useState(0);
    const [sourceTimeSec, setSourceTimeSec] = useState(0);
    const [activeSegmentIndex, setActiveSegmentIndex] = useState(0);
    const activeSegmentIndexRef = useRef(0);
    const isPlayingRef = useRef(false);
    const outputTimeRef = useRef(0);
    const lastAppliedSegmentSignatureRef = useRef<string | null>(null);
    const pendingAutoplayRef = useRef(false);
    const pendingSegmentSeekRef = useRef<{
      sourceTimeSec: number;
      autoplay: boolean;
      shouldEmitState: boolean;
    } | null>(null);

    const activeSegments = useMemo(
      () => segments.filter(segment => !segment.isDeleted),
      [segments],
    );

    useEffect(() => {
      if (
        process.env.NODE_ENV === 'test' ||
        process.env.JEST_WORKER_ID != null
      ) {
        return;
      }
      void import('media-chrome');
      void import('hls-video-element');
    }, []);

    const activeSegment = activeSegments[activeSegmentIndex] ?? null;
    const activeSegmentSignature = activeSegment
      ? `${activeSegment.id}:${activeSegment.originalStartTime}:${activeSegment.originalEndTime}`
      : null;
    const recordingsById = useMemo(
      () => new Map(recordings.map(recording => [recording.id, recording])),
      [recordings],
    );

    // Returns a stable ref callback per segment id. Storing callbacks in a ref
    // map means React.memo on SegmentMediaItem sees the same function reference
    // across renders, preventing spurious re-mounts of <hls-video>.
    const segmentRefCallbacks = useRef<
      Map<string, (node: Element | null) => void>
    >(new Map());
    const getSegmentMediaRef = useCallback((segmentId: string) => {
      let cb = segmentRefCallbacks.current.get(segmentId);
      if (!cb) {
        cb = (node: Element | null) => {
          if (node == null) {
            mediaRefs.current.delete(segmentId);
          } else {
            mediaRefs.current.set(segmentId, node as HTMLVideoElement);
          }
        };
        segmentRefCallbacks.current.set(segmentId, cb);
      }
      return cb;
    }, []);

    const syncActiveMediaRef = useCallback(() => {
      if (!activeSegment) {
        videoRef.current = null;
        setVideoElementForOverlay(null);
        return;
      }
      const media = mediaRefs.current.get(activeSegment.id) ?? null;
      videoRef.current = media;
      setVideoElementForOverlay(media);
    }, [activeSegment]);

    const pauseNonActivePlayers = useCallback((activeSegmentId: string) => {
      mediaRefs.current.forEach((media, segmentId) => {
        if (segmentId !== activeSegmentId) {
          pauseMedia(media);
        }
      });
    }, []);

    const emitPlaybackState = useCallback(
      (
        nextOutputTime: number,
        nextIsPlaying: boolean,
        nextSourceTimeSec?: number,
        nextRecordingId?: string,
      ) => {
        const active = activeSegments[activeSegmentIndexRef.current];
        const resolvedSource =
          nextSourceTimeSec ?? videoRef.current?.currentTime ?? 0;
        const resolvedRecordingId =
          nextRecordingId ?? active?.recordingId ?? '';
        setSourceTimeSec(resolvedSource);
        onPlaybackStateChange?.({
          outputTime: nextOutputTime,
          sourceTimeSec: resolvedSource,
          recordingId: resolvedRecordingId,
          isPlaying: nextIsPlaying,
        });
      },
      [activeSegments, onPlaybackStateChange],
    );

    const setCurrentSegment = useCallback(
      (
        nextSegmentIndex: number,
        nextSourceTimeSec: number,
        autoplay = false,
        shouldEmitState = true,
      ) => {
        pendingAutoplayRef.current = autoplay;
        const currentSegmentIndex = activeSegmentIndexRef.current;
        const nextSegment = activeSegments[nextSegmentIndex];
        if (!nextSegment) {
          return;
        }

        const sourceTime = Math.max(0, nextSourceTimeSec);
        if (nextSegmentIndex === currentSegmentIndex && videoRef.current) {
          videoRef.current.currentTime = sourceTime;
          if (autoplay) {
            playMedia(videoRef.current);
          } else {
            pauseMedia(videoRef.current);
          }
          if (shouldEmitState) {
            const sourceMs = Math.min(
              sourceTime * 1000,
              nextSegment.originalEndTime,
            );
            const sourceOffsetMs = sourceMs - nextSegment.originalStartTime;
            const nextOutputTimeSec = Math.max(
              0,
              Math.min(
                outputDuration,
                toSeconds(nextSegment.outputStartTime + sourceOffsetMs),
              ),
            );
            setOutputTime(nextOutputTimeSec);
            emitPlaybackState(
              nextOutputTimeSec,
              autoplay,
              sourceTime,
              nextSegment.recordingId,
            );
          }
          pendingSegmentSeekRef.current = null;
          return;
        }

        pendingSegmentSeekRef.current = {
          sourceTimeSec: sourceTime,
          autoplay,
          shouldEmitState,
        };
        activeSegmentIndexRef.current = nextSegmentIndex;
        setActiveSegmentIndex(nextSegmentIndex);
      },
      [activeSegments, emitPlaybackState, outputDuration],
    );

    useEffect(() => {
      activeSegmentIndexRef.current = activeSegmentIndex;
    }, [activeSegmentIndex]);

    useEffect(() => {
      syncActiveMediaRef();
      if (!activeSegment) {
        return;
      }
      pauseNonActivePlayers(activeSegment.id);
    }, [activeSegment, pauseNonActivePlayers, syncActiveMediaRef]);

    useEffect(() => {
      isPlayingRef.current = isPlaying;
    }, [isPlaying]);

    useEffect(() => {
      outputTimeRef.current = outputTime;
    }, [outputTime]);

    useEffect(() => {
      if (activeSegmentIndex < activeSegments.length) {
        return;
      }
      activeSegmentIndexRef.current = 0;
      setActiveSegmentIndex(0);
    }, [activeSegmentIndex, activeSegments.length]);

    useImperativeHandle(
      ref,
      () => ({
        play: () => {
          const media = videoRef.current;
          if (!media) return;
          playMedia(media);
        },
        pause: () => {
          const media = videoRef.current;
          if (!media) return;
          pauseMedia(media);
        },
        seek: (nextOutputTime: number) => {
          const boundedOutputTime = Math.max(
            0,
            Math.min(outputDuration, nextOutputTime),
          );
          const location = findSegmentAtOutputTime(
            activeSegments,
            boundedOutputTime,
          );
          if (!location) {
            return;
          }

          pendingAutoplayRef.current = isPlayingRef.current;
          setCurrentSegment(
            location.segmentIndex,
            location.sourceTimeSec,
            isPlayingRef.current,
          );
          setOutputTime(boundedOutputTime);
          const segment = activeSegments[location.segmentIndex];
          emitPlaybackState(
            boundedOutputTime,
            isPlayingRef.current,
            location.sourceTimeSec,
            segment?.recordingId,
          );
        },
        previewSegmentTime: (segmentId: string, sourceTimeSec: number) => {
          const segmentIndex = activeSegments.findIndex(
            segment => segment.id === segmentId,
          );
          if (segmentIndex < 0) {
            return;
          }
          setCurrentSegment(segmentIndex, sourceTimeSec, false, false);
        },
      }),
      [activeSegments, emitPlaybackState, outputDuration, setCurrentSegment],
    );

    useEffect(() => {
      const media = videoRef.current;
      if (!media || !activeSegment) {
        return;
      }

      const handlePlay = () => {
        setIsPlaying(true);
        emitPlaybackState(outputTimeRef.current, true);
      };
      const handlePause = () => {
        setIsPlaying(false);
        emitPlaybackState(outputTimeRef.current, false);
      };
      const handleLoadedMetadata = () => {
        if (!pendingAutoplayRef.current) {
          return;
        }
        pendingAutoplayRef.current = false;
        playMedia(media);
      };
      const handleTimeUpdate = () => {
        const currentActiveIndex = activeSegmentIndexRef.current;
        const active = activeSegments[currentActiveIndex];
        if (!active) return;

        const currentSourceMs = media.currentTime * 1000;
        const boundedSourceMs = Math.min(
          currentSourceMs,
          active.originalEndTime,
        );
        const sourceOffsetMs = boundedSourceMs - active.originalStartTime;
        const nextOutputTimeSec = Math.max(
          0,
          Math.min(
            outputDuration,
            toSeconds(active.outputStartTime + sourceOffsetMs),
          ),
        );
        setOutputTime(nextOutputTimeSec);
        emitPlaybackState(
          nextOutputTimeSec,
          !media.paused,
          boundedSourceMs / 1000,
          active.recordingId,
        );
        if (currentSourceMs >= active.originalEndTime) {
          const nextSegmentIndex = currentActiveIndex + 1;
          const nextSegment = activeSegments[nextSegmentIndex];
          if (!nextSegment) {
            pauseMedia(media);
            setOutputTime(outputDuration);
            emitPlaybackState(outputDuration, false);
            return;
          }
          setCurrentSegment(
            nextSegmentIndex,
            toSeconds(nextSegment.originalStartTime),
            !media.paused,
          );
        }
      };

      media.addEventListener('play', handlePlay);
      media.addEventListener('pause', handlePause);
      media.addEventListener('loadedmetadata', handleLoadedMetadata);
      media.addEventListener('timeupdate', handleTimeUpdate);

      return () => {
        media.removeEventListener('play', handlePlay);
        media.removeEventListener('pause', handlePause);
        media.removeEventListener('loadedmetadata', handleLoadedMetadata);
        media.removeEventListener('timeupdate', handleTimeUpdate);
      };
    }, [
      activeSegment,
      activeSegments,
      emitPlaybackState,
      outputDuration,
      setCurrentSegment,
    ]);

    useEffect(() => {
      const media = videoRef.current;
      if (!media || !activeSegment) {
        return;
      }

      const pendingSeek = pendingSegmentSeekRef.current;
      if (
        pendingSeek == null &&
        lastAppliedSegmentSignatureRef.current === activeSegmentSignature
      ) {
        return;
      }
      // When there is no pending seek (e.g. a split or trim changed the segment
      // shape without a user-initiated segment switch), preserve the current
      // video position rather than jumping to the segment start.
      // Guard against NaN/Infinity from HLS media that hasn't loaded yet.
      const safeCurrentTime = Number.isFinite(media.currentTime)
        ? media.currentTime
        : toSeconds(activeSegment.originalStartTime);
      const nextSourceTimeSec = pendingSeek
        ? Math.max(0, pendingSeek.sourceTimeSec)
        : safeCurrentTime;
      media.currentTime = nextSourceTimeSec;
      if (pendingSeek?.autoplay) {
        playMedia(media);
      } else {
        pauseMedia(media);
      }

      if (pendingSeek?.shouldEmitState) {
        const sourceMs = Math.min(
          nextSourceTimeSec * 1000,
          activeSegment.originalEndTime,
        );
        const sourceOffsetMs = sourceMs - activeSegment.originalStartTime;
        const nextOutputTimeSec = Math.max(
          0,
          Math.min(
            outputDuration,
            toSeconds(activeSegment.outputStartTime + sourceOffsetMs),
          ),
        );
        setOutputTime(nextOutputTimeSec);
        emitPlaybackState(
          nextOutputTimeSec,
          pendingSeek.autoplay,
          nextSourceTimeSec,
          activeSegment.recordingId,
        );
      }

      pendingSegmentSeekRef.current = null;
      lastAppliedSegmentSignatureRef.current = activeSegmentSignature;
    }, [
      activeSegment,
      activeSegmentSignature,
      emitPlaybackState,
      outputDuration,
    ]);

    const segmentMediaItems = useMemo(
      () =>
        activeSegments.map((segment, index) => {
          const recording = recordingsById.get(segment.recordingId);
          if (!recording) return null;

          return (
            <SegmentMediaItem
              key={segment.id}
              segment={segment}
              recording={recording}
              isActive={index === activeSegmentIndex}
              isPortrait={isPortrait}
              isBlurMode={isBlurMode}
              onRef={getSegmentMediaRef(segment.id)}
            />
          );
        }),
      [
        activeSegments,
        activeSegmentIndex,
        recordingsById,
        isPortrait,
        isBlurMode,
        getSegmentMediaRef,
      ],
    );

    if (!activeSegment) {
      return (
        <div
          className={className}
          data-testid="segment-video-player-empty"
          aria-label={t('content-recording.editor-v2.segment-player-empty')}
        />
      );
    }

    const playPauseOverlay = (
      <div className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center">
        {!isPlaying ? (
          <button
            type="button"
            className="pointer-events-auto flex h-16 w-16 items-center justify-center rounded-full bg-black/35 text-white backdrop-blur-sm transition-colors hover:bg-black/50"
            onClick={() => playMedia(videoRef.current)}
            aria-label={t('content-recording.editor-v2.play')}
          >
            <PlayFilled className="ml-0.5 h-8 w-8" />
          </button>
        ) : (
          <button
            type="button"
            className="pointer-events-auto flex h-16 w-16 items-center justify-center rounded-full bg-black/35 text-white backdrop-blur-sm opacity-0 transition-all group-hover:opacity-100 hover:bg-black/50"
            onClick={() => pauseMedia(videoRef.current)}
            aria-label={t('content-recording.editor-v2.pause')}
          >
            <PauseFilled className="h-8 w-8" />
          </button>
        )}
      </div>
    );

    const playerViewport = isPortrait ? (
      <div className="flex min-h-0 max-h-full w-full max-w-full flex-1 items-center justify-center">
        <div className="group relative aspect-[9/16] h-full max-h-full max-w-full overflow-hidden rounded">
          <BlurredVideoOverlay
            videoElement={videoElementForOverlay}
            enabled={isBlurMode}
          />
          {!isBlurMode && (
            <div
              className="absolute inset-0 z-0"
              style={{ backgroundColor: portraitBgColor }}
            />
          )}
          <EditCaptionOverlay sourceTimeSec={sourceTimeSec} />
          {segmentMediaItems}
          {playPauseOverlay}
        </div>
      </div>
    ) : (
      <div className="flex aspect-video max-h-full min-h-0 w-full max-w-full flex-1 flex-col items-center justify-center">
        <div className="group relative h-full w-full overflow-hidden rounded">
          <EditCaptionOverlay sourceTimeSec={sourceTimeSec} />
          {segmentMediaItems}
          {playPauseOverlay}
        </div>
      </div>
    );

    return (
      <div
        className={cx('flex h-full min-h-0 flex-col', className)}
        data-testid="segment-video-player"
      >
        {playerViewport}
        <SegmentVideoPlayerControls
          zoomScale={zoomScale}
          onZoomScaleChange={onZoomScaleChange}
          onSplitAtPlayhead={onSplitAtPlayhead}
          canSplitAtPlayhead={canSplitAtPlayhead}
          hints={hints}
        />
      </div>
    );
  },
);

SegmentVideoPlayer.displayName = 'SegmentVideoPlayer';
